/**
 * The SNMP credential fields, as one control.
 *
 * **WHY ONE TYPE RATHER THAN FOUR SETS OF FIELDS.** Four panels ask for these - the Network page's
 * adopt panel, the device page's adopt panel, the Synology enrollment form, and a monitor's edit
 * panel - and in the OpenSilver client each carried its own copy of the fields and its own copy of
 * the credential-building logic. All four hardcoded SHA-1 and AES-128 and *inferred* the security
 * level from which password boxes happened to be filled in, which made MD5, DES and an explicit
 * `authNoPriv` unreachable from anywhere in the product despite the agent implementing all three -
 * and meant a fix had to be made in four places to count.
 *
 * **The security level is explicit**, for the reason that inference failed twice: `authNoPriv` could
 * not be configured on a device whose privacy password you happened to know, and a v3 user with no
 * auth password silently became `noAuthNoPriv` - a form that looked fine and produced a monitor the
 * device refuses, with nothing saying why.
 *
 * **The port is stored as 0 when it is 161.** A credential blob written before the field existed
 * deserializes its absent members to 0, and storing 161 would have made those blobs point at an
 * unreachable port 0. The box shows 161 rather than blank so the field says what it is doing.
 */
import type { Control, StackPanel } from 'webwin';
import {
  SnmpAuthProtocol,
  SnmpPrivProtocol,
  SnmpSecurityLevel,
  SnmpVersion,
  type SnmpCredentials,
  type SnmpCredentialShape
} from '../contracts.gen.js';
import {
  buttonRow,
  checkBox,
  comboBox,
  field,
  fieldRow,
  GAP,
  passwordBox,
  stackOf,
  textBox,
  vstack
} from './layout.js';

const LEVELS: readonly { readonly label: string; readonly level: SnmpSecurityLevel }[] = [
  { label: 'noAuthNoPriv', level: SnmpSecurityLevel.NoAuthNoPriv },
  { label: 'authNoPriv', level: SnmpSecurityLevel.AuthNoPriv },
  { label: 'authPriv', level: SnmpSecurityLevel.AuthPriv }
];

// SHA-1 first because it is the sane default and what every previous version of this form silently
// used; MD5 is offered because plenty of older gear only speaks it.
const AUTH: readonly { readonly label: string; readonly protocol: SnmpAuthProtocol }[] = [
  { label: 'SHA-1', protocol: SnmpAuthProtocol.Sha1 },
  { label: 'MD5', protocol: SnmpAuthProtocol.Md5 }
];

const PRIV: readonly { readonly label: string; readonly protocol: SnmpPrivProtocol }[] = [
  { label: 'AES-128', protocol: SnmpPrivProtocol.Aes128 },
  { label: 'DES', protocol: SnmpPrivProtocol.Des }
];

/** What `build` returns: the credentials, or the reason it could not make them. */
export type SnmpBuild =
  | { readonly ok: true; readonly credentials: SnmpCredentials }
  | { readonly ok: false; readonly error: string };

export class SnmpForm {
  readonly panel: StackPanel;

  readonly #v3 = checkBox('Use SNMPv3');
  readonly #port = textBox({ text: '161', width: 70, autoWidth: false, hAlign: 'start' });
  readonly #community = textBox({ text: 'public', width: 180, autoWidth: false, hAlign: 'start' });
  readonly #user = textBox({ width: 180, autoWidth: false, hAlign: 'start' });
  readonly #level = comboBox(LEVELS, {
    itemText: l => l.label,
    // authPriv, the strongest, so the form's default is the one a security-conscious device wants.
    selectedIndex: 2,
    width: 180,
    autoWidth: false,
    hAlign: 'start'
  });
  readonly #authProtocol = comboBox(AUTH, {
    itemText: a => a.label,
    selectedIndex: 0,
    width: 180,
    autoWidth: false,
    hAlign: 'start'
  });
  readonly #authPassword = passwordBox({ width: 180, autoWidth: false, hAlign: 'start' });
  readonly #privProtocol = comboBox(PRIV, {
    itemText: p => p.label,
    selectedIndex: 0,
    width: 180,
    autoWidth: false,
    hAlign: 'start'
  });
  readonly #privPassword = passwordBox({ width: 180, autoWidth: false, hAlign: 'start' });

  readonly #communityRow: Control;
  readonly #authPasswordField: Control;
  readonly #privPasswordField: Control;
  #secretsShown = true;
  readonly #v3Rows: StackPanel;
  readonly #authRows: StackPanel;
  readonly #privRows: StackPanel;

  constructor() {
    this.#communityRow = field('Community', this.#community);

    this.#authPasswordField = field('Auth password', this.#authPassword);
    this.#privPasswordField = field('Privacy password', this.#privPassword);
    this.#authRows = stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      fieldRow(field('Auth', this.#authProtocol), this.#authPasswordField)
    );
    this.#privRows = stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      fieldRow(field('Privacy', this.#privProtocol), this.#privPasswordField)
    );
    this.#v3Rows = stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      fieldRow(field('User', this.#user), field('Security', this.#level)),
      this.#authRows,
      this.#privRows
    );

    this.panel = stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      // The version tick sits beside the port rather than in a captioned field: a checkbox carries
      // its own label, and a blank caption above it would read as a field that failed to load.
      stackOf(buttonRow({ spacing: GAP.lg, align: 'end' }), field('UDP port', this.#port), this.#v3),
      this.#communityRow,
      this.#v3Rows
    );

    this.#v3.on('change', () => this.#applyVisibility());
    this.#level.on('selectionchanged', () => this.#applyVisibility());
    this.#applyVisibility();
  }

  #applyVisibility(): void {
    const v3 = this.#v3.checked;
    this.#communityRow.visible = !v3 && this.#secretsShown;
    this.#authPasswordField.visible = this.#secretsShown;
    this.#privPasswordField.visible = this.#secretsShown;
    this.#v3Rows.visible = v3;
    const level = this.#level.selectedItem?.level ?? SnmpSecurityLevel.AuthPriv;
    // Auth applies at authNoPriv and authPriv; privacy at authPriv only.
    this.#authRows.visible = v3 && level !== SnmpSecurityLevel.NoAuthNoPriv;
    this.#privRows.visible = v3 && level === SnmpSecurityLevel.AuthPriv;
    this.panel.layout();
  }

  /**
   * The credentials this form describes.
   *
   * Validated here **as well as** server-side: the server is the authority, since a client can be
   * bypassed, but a technician should be told what is missing before paying for a round trip.
   */
  build(): SnmpBuild {
    const typed = Number.parseInt(this.#port.text.trim(), 10);
    const port = Number.isFinite(typed) && typed >= 1 && typed <= 65535 ? typed : 161;
    const wirePort = port === 161 ? 0 : port;

    if (!this.#v3.checked) {
      const community = this.#community.text.trim() || 'public';
      return {
        ok: true,
        credentials: {
          version: SnmpVersion.V2c,
          community,
          securityLevel: SnmpSecurityLevel.NoAuthNoPriv,
          securityName: '',
          authProtocol: SnmpAuthProtocol.None,
          authPassword: '',
          privProtocol: SnmpPrivProtocol.None,
          privPassword: '',
          port: wirePort
        }
      };
    }

    const user = this.#user.text.trim();
    if (!user) return { ok: false, error: 'Enter the SNMPv3 user name.' };

    const level = this.#level.selectedItem?.level ?? SnmpSecurityLevel.AuthPriv;
    const authPassword = this.#authPassword.text;
    const privPassword = this.#privPassword.text;

    if (level !== SnmpSecurityLevel.NoAuthNoPriv && !authPassword) {
      return { ok: false, error: 'Enter the authentication password, or choose noAuthNoPriv.' };
    }
    if (level === SnmpSecurityLevel.AuthPriv && !privPassword) {
      return { ok: false, error: 'Enter the privacy password, or choose authNoPriv.' };
    }

    return {
      ok: true,
      credentials: {
        version: SnmpVersion.V3,
        community: '',
        securityLevel: level,
        securityName: user,
        authProtocol:
          level === SnmpSecurityLevel.NoAuthNoPriv
            ? SnmpAuthProtocol.None
            : (this.#authProtocol.selectedItem?.protocol ?? SnmpAuthProtocol.Sha1),
        authPassword,
        privProtocol:
          level === SnmpSecurityLevel.AuthPriv
            ? (this.#privProtocol.selectedItem?.protocol ?? SnmpPrivProtocol.Aes128)
            : SnmpPrivProtocol.None,
        privPassword,
        port: wirePort
      }
    };
  }

  /** The non-secret settings alone, for an edit that changes a port or an algorithm. */
  buildShape(): SnmpCredentialShape {
    const typed = Number.parseInt(this.#port.text.trim(), 10);
    const port = Number.isFinite(typed) && typed >= 1 && typed <= 65535 ? typed : 161;
    const v3 = this.#v3.checked;
    const level = this.#level.selectedItem?.level ?? SnmpSecurityLevel.AuthPriv;
    return {
      version: v3 ? SnmpVersion.V3 : SnmpVersion.V2c,
      securityLevel: v3 ? level : SnmpSecurityLevel.NoAuthNoPriv,
      authProtocol:
        v3 && level !== SnmpSecurityLevel.NoAuthNoPriv
          ? (this.#authProtocol.selectedItem?.protocol ?? SnmpAuthProtocol.Sha1)
          : SnmpAuthProtocol.None,
      privProtocol:
        v3 && level === SnmpSecurityLevel.AuthPriv
          ? (this.#privProtocol.selectedItem?.protocol ?? SnmpPrivProtocol.Aes128)
          : SnmpPrivProtocol.None,
      securityName: v3 ? this.#user.text.trim() : '',
      port: port === 161 ? 0 : port
    };
  }

  /**
   * Fills the form in from what is stored, so an edit panel opens describing the device as it
   * actually is rather than as the defaults.
   *
   * The community is **not** seeded and stays at the form's default: it is a secret and is never
   * returned, so an edit that switches to v2c has to state it. Blanking the box here would be worse
   * - it would look as though the stored value were empty.
   */
  seed(shape: SnmpCredentialShape | null): void {
    const v3 = shape?.version === SnmpVersion.V3;
    this.#v3.checked = v3;
    this.#user.text = shape?.securityName ?? '';
    const port = shape?.port ?? 0;
    this.#port.text = String(port >= 1 && port <= 65535 ? port : 161);
    this.#level.selectedIndex = Math.max(
      0,
      LEVELS.findIndex(l => l.level === (shape?.securityLevel ?? SnmpSecurityLevel.AuthPriv))
    );
    this.#authProtocol.selectedIndex = shape?.authProtocol === SnmpAuthProtocol.Md5 ? 1 : 0;
    this.#privProtocol.selectedIndex = shape?.privProtocol === SnmpPrivProtocol.Des ? 1 : 0;
    this.#applyVisibility();
  }

  /**
   * Shows or hides the community and the two passwords.
   *
   * A monitor's edit panel keeps the stored secrets unless "Replace the SNMP passwords" is ticked, and
   * a community box still reading "public" beside that would look like the stored value - which it is
   * not, and which is never returned. Hidden, the form describes only what an edit changes.
   */
  showSecrets(show: boolean): void {
    this.#secretsShown = show;
    this.#applyVisibility();
  }

  /**
   * Starts the community at the one a scan actually got an answer with. On a site whose devices are
   * not on "public", the hardcoded default meant every adopt was born with credentials that could
   * never poll.
   */
  setCommunity(community: string | null | undefined): void {
    if (community?.trim()) this.#community.text = community.trim();
  }

  /** Clears both passwords. Called after a submit: neither is ever retained. */
  clearPasswords(): void {
    this.#authPassword.text = '';
    this.#privPassword.text = '';
  }
}
